import { useState, useRef, useEffect } from "react";
import EmojiPicker from "emoji-picker-react";
import {
  SendIcon,
  MicrophoneIcon,
  AttachIcon,
  EmojiIcon,
  ImageIcon,
  DocumentIcon,
  LocationIcon,
  CloseIcon,
} from "../../../svg/chat";
import {
  uploadImages,
  uploadAudio,
  uploadDocument,
} from "../../../functions/uploadMedia";
import "./style.css";

export default function MessageInput({
  onSendMessage,
  onTypingStart,
  onTypingStop,
  replyTo,
  onCancelReply,
  user,
}) {
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (replyTo) {
      inputRef.current?.focus();
    }
  }, [replyTo]);

  const handleTextChange = (e) => {
    setText(e.target.value);

    // Typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    } else {
      onTypingStart();
    }

    typingTimeoutRef.current = setTimeout(() => {
      onTypingStop();
      typingTimeoutRef.current = null;
    }, 2000);
  };

  const handleSend = () => {
    if (!text.trim()) return;

    onSendMessage({
      type: "text",
      text: text.trim(),
    });

    setText("");
    setShowEmoji(false);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
      onTypingStop();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (emojiData) => {
    setText((prev) => prev + emojiData.emoji);
    inputRef.current?.focus();
  };

  const handleFileSelect = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Close attach menu
    setShowAttach(false);
    setIsUploading(true);

    try {
      if (type === "media") {
        // Separate images and videos
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        const videoFiles = files.filter((f) => f.type.startsWith("video/"));

        // Upload images
        if (imageFiles.length > 0) {
          const uploadResult = await uploadImages(imageFiles, user.token, "chat");
          if (uploadResult && uploadResult.length > 0) {
            onSendMessage({
              type: "image",
              media: uploadResult.map((item) =>
                typeof item === "string" ? { url: item } : item
              ),
            });
          }
        }

        // Upload videos
        for (const videoFile of videoFiles) {
          const uploadResult = await uploadImages([videoFile], user.token, "chat/video");
          if (uploadResult && uploadResult.length > 0) {
            onSendMessage({
              type: "video",
              media: uploadResult.map((item) =>
                typeof item === "string" ? { url: item } : item
              ),
            });
          }
        }
      } else if (type === "document") {
        // Upload documents - new endpoint returns { url, name, size, mimeType }
        for (const file of files) {
          const uploadResult = await uploadDocument(file, user.token);
          if (uploadResult && uploadResult.length > 0) {
            onSendMessage({
              type: "document",
              media: uploadResult, // Already contains { url, name, size, mimeType }
            });
          }
        }
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Erro ao enviar arquivo. Tente novamente.");
    } finally {
      setIsUploading(false);
      // Clear file input
      e.target.value = "";
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Não foi possível acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);

      // Stop the recorder and wait for it to finish
      mediaRecorderRef.current.stop();

      // Wait for the last data chunk
      await new Promise((resolve) => {
        mediaRecorderRef.current.onstop = resolve;
      });

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Check if we have audio data
      if (audioChunksRef.current.length === 0) {
        console.log("No audio data recorded");
        return;
      }

      // Create blob from chunks
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });

      // Upload audio
      setIsUploading(true);
      try {
        const uploadResult = await uploadAudio(audioBlob, user.token);
        if (uploadResult && uploadResult.length > 0) {
          onSendMessage({
            type: "audio",
            media: uploadResult.map((item) =>
              typeof item === "string" ? { url: item } : item
            ),
          });
        }
      } catch (error) {
        console.error("Error uploading audio:", error);
        alert("Erro ao enviar áudio. Tente novamente.");
      } finally {
        setIsUploading(false);
        audioChunksRef.current = [];
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      audioChunksRef.current = [];
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleLocationShare = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          onSendMessage({
            type: "location",
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
          });
          setShowAttach(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Could not get your location. Please check permissions.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  return (
    <div className="message_input_container">
      {/* Uploading indicator */}
      {isUploading && (
        <div className="uploading_indicator">
          <div className="uploading_spinner"></div>
          <span>A enviar...</span>
        </div>
      )}

      {/* Reply preview */}
      {replyTo && (
        <div className="reply_input_preview">
          <div className="reply_bar"></div>
          <div className="reply_info">
            <span className="reply_to">
              Replying to {replyTo.sender?.first_name || "Unknown"}
            </span>
            <span className="reply_text">
              {replyTo.type !== "text"
                ? `📎 ${replyTo.type}`
                : replyTo.text?.substring(0, 50)}
            </span>
          </div>
          <button className="cancel_reply" onClick={onCancelReply}>
            <CloseIcon />
          </button>
        </div>
      )}

      {/* Recording UI */}
      {isRecording ? (
        <div className="recording_container">
          <button className="cancel_recording" onClick={cancelRecording}>
            <CloseIcon color="#e41e3f" />
          </button>
          <div className="recording_indicator">
            <span className="recording_dot"></span>
            <span className="recording_time">
              {formatRecordingTime(recordingTime)}
            </span>
          </div>
          <button className="stop_recording" onClick={stopRecording}>
            <SendIcon />
          </button>
        </div>
      ) : (
        <div className="input_wrapper">
          {/* Emoji picker */}
          <div className="emoji_wrapper">
            <button
              className="input_action_btn"
              onClick={() => {
                setShowEmoji(!showEmoji);
                setShowAttach(false);
              }}
            >
              <EmojiIcon />
            </button>
            {showEmoji && (
              <div className="emoji_picker_container">
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  width={320}
                  height={400}
                />
              </div>
            )}
          </div>

          {/* Attach menu */}
          <div className="attach_wrapper">
            <button
              className="input_action_btn"
              onClick={() => {
                setShowAttach(!showAttach);
                setShowEmoji(false);
              }}
            >
              <AttachIcon />
            </button>
            {showAttach && (
              <div className="attach_menu">
                <button
                  className="attach_option"
                  onClick={() => imageInputRef.current?.click()}
                >
                  <div className="attach_icon" style={{ background: "#7c3aed" }}>
                    <ImageIcon color="#fff" />
                  </div>
                  <span>Photos & Videos</span>
                </button>
                <button
                  className="attach_option"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="attach_icon" style={{ background: "#5468ff" }}>
                    <DocumentIcon color="#fff" />
                  </div>
                  <span>Document</span>
                </button>
                <button className="attach_option" onClick={handleLocationShare}>
                  <div className="attach_icon" style={{ background: "#29b6f6" }}>
                    <LocationIcon color="#fff" />
                  </div>
                  <span>Location</span>
                </button>
              </div>
            )}
          </div>

          {/* Hidden file inputs */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            hidden
            onChange={(e) => handleFileSelect(e, "media")}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            multiple
            hidden
            onChange={(e) => handleFileSelect(e, "document")}
          />

          {/* Text input */}
          <textarea
            ref={inputRef}
            className="message_textarea"
            placeholder="Type a message..."
            value={text}
            onChange={handleTextChange}
            onKeyPress={handleKeyPress}
            rows={1}
          />

          {/* Send or record button */}
          {text.trim() ? (
            <button className="send_btn" onClick={handleSend}>
              <SendIcon />
            </button>
          ) : (
            <button className="record_btn" onClick={startRecording}>
              <MicrophoneIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
